import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Loader2, MailCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ThemeToggle } from "@/components/ThemeToggle";
import { SeoHead } from "@/components/SeoHead";
import { NAV, AUTH } from "@/constants/testIds";
import { useSession, formatApiErrorDetail } from "@/context/AuthContext";
import { MagicLinkForm } from "@/components/auth/MagicLinkForm";
import { SemaLogo } from "@/components/marketing/SemaLogo";

export default function Signup() {
  const navigate = useNavigate();
  const { register, resendVerification } = useSession();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(null); // { email, verification_link? }
  const [resendMsg, setResendMsg] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await register(name, email, password);
      if (res?.status === "verification_required") {
        setSent(res);
      } else {
        navigate("/onboarding");
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const onResend = async () => {
    setResendMsg("");
    try {
      const res = await resendVerification(sent.email);
      setSent((s) => ({ ...s, verification_link: res.verification_link || s.verification_link }));
      setResendMsg("Sent — check your inbox for the new link.");
    } catch (err) {
      setResendMsg(formatApiErrorDetail(err.response?.data?.detail) || "Could not resend. Try again shortly.");
    }
  };

  return (
    <div
      data-testid={AUTH.signupRoot}
      className="relative min-h-screen w-full bg-background text-foreground"
    >
      <SeoHead
        title="Start Free — Sema Sandbox"
        description="Create a free Sema workspace: connect a source or upload a CSV and ask your first governed, plain-English question in minutes."
        path="/signup"
      />
      <div
        aria-hidden="true"
        className="sema-grid pointer-events-none absolute inset-0 opacity-30"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-24 left-1/2 h-[380px] w-[600px] -translate-x-1/2 rounded-full blur-3xl opacity-20 dark:opacity-30"
        style={{
          background: "radial-gradient(closest-side, hsl(var(--accent)), transparent)",
        }}
      />

      <header className="relative z-10 mx-auto flex w-full max-w-6xl items-center justify-between px-6 py-6">
        <SemaLogo to="/" testid={NAV.brand} imgClassName="h-8" />
        <ThemeToggle />
      </header>

      <main className="relative z-10 mx-auto flex min-h-[calc(100vh-160px)] w-full max-w-md flex-col justify-center px-6">
        <p className="mb-5 inline-flex w-fit items-center gap-2 rounded-full border border-border/60 bg-card/60 px-3 py-1 font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground backdrop-blur">
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-accent" />
          sandbox · free
        </p>

        <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">Spin up a sandbox.</h1>
        <div aria-hidden="true" className="sema-accent-underline mt-5" />

        {sent ? (
          <div data-testid={AUTH.signupVerifyPanel} className="mt-8">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-accent/15 text-accent">
                <MailCheck className="h-6 w-6" />
              </div>
              <h2 className="text-xl font-semibold tracking-tight">Check your inbox</h2>
            </div>
            <p className="mt-5 text-sm leading-relaxed text-muted-foreground">
              We sent a verification link to{" "}
              <span className="font-medium text-foreground">{sent.email}</span>. Click it to
              activate your account, then sign in.
            </p>

            {sent.verification_link && (
              <a
                href={sent.verification_link}
                data-testid={AUTH.signupVerifyLink}
                className="group mt-6 inline-flex h-11 items-center justify-center rounded-full sema-cta-gradient px-6 font-mono text-xs uppercase tracking-[0.22em] text-white shadow-lg shadow-sema-primary/25 transition-transform hover:-translate-y-0.5"
              >
                Verify now
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </a>
            )}

            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Button
                variant="outline"
                data-testid={AUTH.signupResend}
                onClick={onResend}
                className="rounded-full border-border/70 font-mono text-[11px] uppercase tracking-[0.2em]"
              >
                Resend link
              </Button>
              {resendMsg && (
                <span data-testid={AUTH.signupResendMsg} className="text-xs text-muted-foreground">
                  {resendMsg}
                </span>
              )}
            </div>

            <p className="mt-8 text-sm text-muted-foreground">
              Already verified?{" "}
              <Link to="/login" className="text-accent hover:underline">
                Sign in
              </Link>
            </p>
          </div>
        ) : (
        <>
        <form onSubmit={onSubmit} className="mt-8 space-y-5">
          <div className="space-y-2">
            <Label htmlFor="name" className="font-mono text-xs uppercase tracking-[0.18em] text-muted-foreground">
              Name
            </Label>
            <Input
              id="name"
              type="text"
              required
              autoComplete="name"
              data-testid={AUTH.signupName}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ada Lovelace"
              className="h-11 bg-card/60"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email" className="font-mono text-xs uppercase tracking-[0.18em] text-muted-foreground">
              Email
            </Label>
            <Input
              id="email"
              type="email"
              required
              autoComplete="email"
              data-testid={AUTH.signupEmail}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              className="h-11 bg-card/60"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password" className="font-mono text-xs uppercase tracking-[0.18em] text-muted-foreground">
              Password
            </Label>
            <Input
              id="password"
              type="password"
              required
              autoComplete="new-password"
              data-testid={AUTH.signupPassword}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 8 characters"
              className="h-11 bg-card/60"
            />
          </div>

          {error && (
            <p data-testid={AUTH.signupError} className="text-sm text-destructive">
              {error}
            </p>
          )}

          <Button
            type="submit"
            disabled={loading}
            data-testid={AUTH.signupSubmit}
            className="group h-12 w-full rounded-full sema-cta-gradient font-mono text-xs uppercase tracking-[0.22em] text-white shadow-lg shadow-sema-primary/25 hover:-translate-y-0.5 transition-transform"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                Create account
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </>
            )}
          </Button>
        </form>

        <MagicLinkForm label="Or get a passwordless sign-in link" />

        <p className="mt-6 text-sm text-muted-foreground">
          Already have an account?{" "}
          <Link
            to="/login"
            data-testid={AUTH.signupToLogin}
            className="text-accent hover:underline"
          >
            Sign in
          </Link>
        </p>
        </>
        )}

        <Link
          to="/"
          className="mt-10 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.22em] text-muted-foreground hover:text-accent transition-colors"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          back to home
        </Link>
      </main>
    </div>
  );
}
